import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { wsManuscriptMathOCRUrl } from "utils/urls";

function createRequestId() {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return `math-ocr-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function useMathOCRWebSocket({ spaceUuid, manuscriptId, nodeId, enabled = true }) {
    const wsRef = useRef(null);
    const pendingMessageRef = useRef(null);
    const currentRequestIdRef = useRef(null);
    const [status, setStatus] = useState("idle");
    const [latex, setLatex] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const connectionReady = useMemo(
        () => enabled && !!spaceUuid && !!manuscriptId && !!nodeId,
        [enabled, manuscriptId, nodeId, spaceUuid],
    );

    const handleSocketMessage = useCallback((event) => {
        const data = JSON.parse(event.data || "{}");
        const type = data?.type;
        const payload = data?.payload || {};
        const requestId = payload?.request_id;

        if (requestId && requestId !== currentRequestIdRef.current) {
            return;
        }

        if (type === "math_ocr.started") {
            setStatus("running");
            setLatex("");
            setErrorMessage("");
            return;
        }

        if (type === "math_ocr.completed") {
            setStatus("completed");
            setLatex(payload.latex || "");
            return;
        }

        if (type === "math_ocr.stopped") {
            setStatus("stopped");
            setLatex(payload.latex || "");
            return;
        }

        if (type === "math_ocr.error") {
            setStatus("error");
            setErrorMessage(payload.message || "수식 이미지 인식에 실패했습니다.");
        }
    }, []);

    const connectWebSocket = useCallback(() => {
        if (!connectionReady || wsRef.current) {
            return;
        }

        const socket = new WebSocket(wsManuscriptMathOCRUrl(spaceUuid, manuscriptId, nodeId));
        wsRef.current = socket;

        socket.onopen = () => {
            if (pendingMessageRef.current) {
                socket.send(JSON.stringify(pendingMessageRef.current));
                pendingMessageRef.current = null;
            }
        };
        socket.onmessage = handleSocketMessage;
        socket.onerror = () => {
            setErrorMessage("수식 OCR 소켓 연결에 실패했습니다.");
        };
        socket.onclose = () => {
            wsRef.current = null;
        };
    }, [connectionReady, handleSocketMessage, manuscriptId, nodeId, spaceUuid]);

    useEffect(() => {
        if (connectionReady) {
            connectWebSocket();
        }

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [connectWebSocket, connectionReady]);

    const sendOrQueue = useCallback(
        (message) => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify(message));
                return;
            }

            pendingMessageRef.current = message;
            connectWebSocket();
        },
        [connectWebSocket],
    );

    const startRequest = useCallback(
        ({ imageDataUrl, mimeType }) => {
            const requestId = createRequestId();
            currentRequestIdRef.current = requestId;
            setStatus("connecting");
            setLatex("");
            setErrorMessage("");

            sendOrQueue({
                action: "start",
                payload: {
                    request_id: requestId,
                    image_data_url: imageDataUrl,
                    mime_type: mimeType,
                },
            });
        },
        [sendOrQueue],
    );

    const stopRequest = useCallback(() => {
        if (!currentRequestIdRef.current) {
            return;
        }

        sendOrQueue({
            action: "stop",
            payload: {
                request_id: currentRequestIdRef.current,
            },
        });
    }, [sendOrQueue]);

    const reset = useCallback(() => {
        currentRequestIdRef.current = null;
        setStatus("idle");
        setLatex("");
        setErrorMessage("");
    }, []);

    return {
        status,
        latex,
        errorMessage,
        isRunning: status === "connecting" || status === "running",
        hasResult: Boolean(latex),
        startRequest,
        stopRequest,
        reset,
    };
}

export default useMathOCRWebSocket;
